import { useState, type ComponentProps, type Ref } from 'react'
import { Alert, Button, CollapsibleSection, Input, Segmented, StatusHint, Textarea } from '@indipia/ui-core'
import type { OrderScheduleResult, OrderVendorRow } from '../../api/orderApi'
import {
  ORDER_LIMITS, ORDER_MESSAGE, checkSendReady, formatPanelHead, getSendAction, isOrderEditable, readOnlyReason,
  type DraftErrors, type DraftItemErrors, type MaterialTaskRowModel, type OrderDraft,
} from '../../constants/materialOrder.logic'
import { OrderDateEditor } from './OrderDateEditor'
import { OrderItemsEditor } from './OrderItemsEditor'
import { OrderNoticePreview } from './OrderNoticePreview'
import { OrderReplyResult } from './OrderReplyResult'
import { OrderStatusLine } from './OrderStatusLine'
import { VendorPicker } from './VendorPicker'

// C-3 팝업 우측 — 선택 공종의 발주정보 작성·회신 결과·알림톡 미리보기·발송 (순서 고정: 머리 → 회신 결과 → 안내 → 작성 → 미리보기 → 발송).
// 입력 초안·저장은 부모(useOrderDraft)가 들고 있고 이 컴포넌트는 표시와 입력 전달만 한다.

export type SaveState = 'idle' | 'saving' | 'saved' | 'error'

/** 서버 VALIDATION_ERROR 를 칸에 붙인 결과(발송·저장 응답) */
export interface ServerFieldErrors {
  items: Record<number, DraftItemErrors>
  deliveryAddress?: string
  orderNote?: string
  liftReqYn?: string
  /** 칸을 못 찾은 검증 오류 — 패널 상단 문구 */
  panel?: string
}

export const EMPTY_SERVER_ERRORS: ServerFieldErrors = { items: {} }

interface Props {
  model: MaterialTaskRowModel
  row: OrderVendorRow
  signal: ComponentProps<typeof OrderStatusLine>['signal']
  vendorPhone: string | null
  draft: OrderDraft
  errors: DraftErrors
  serverErrors: ServerFieldErrors
  saveState: SaveState
  /** saveState 가 error 일 때 표시할 문구 */
  saveError: string | null
  /** 거래처를 방금 바꿔 새 DRAFT 행이 된 경우 */
  freshVendor: boolean
  sending: boolean
  headingRef: Ref<HTMLHeadingElement>
  onDraftChange: (patch: Partial<OrderDraft>) => void
  onRetrySave: () => void
  onSend: () => void
  onResendWithAvailDate: (availDate: string) => void
  onPickVendor: (vendorId: number) => void
  onDatesChange: (patch: { orderDate?: string | null; deliveryDate?: string | null }) => Promise<OrderScheduleResult>
  /** 다음 직접발주 공종으로 이동([작성 완료·다음 공종]). 다음 공종이 없으면 전달하지 않아 버튼을 그리지 않는다 */
  onNextTask?: () => void
}

function mergeItemErrors(local: Record<number, DraftItemErrors>, server: Record<number, DraftItemErrors>): Record<number, DraftItemErrors> {
  const out: Record<number, DraftItemErrors> = { ...local }
  for (const [k, v] of Object.entries(server)) {
    const i = Number(k)
    out[i] = { ...v, ...out[i] }
  }
  return out
}

export function OrderComposePanel(p: Props) {
  const { model, row, draft, errors, serverErrors } = p
  const [pickingOther, setPickingOther] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const editable = isOrderEditable(row.orderStatusCode)
  const lockedText = readOnlyReason(row.orderStatusCode)
  const action = getSendAction(row)
  const ready = checkSendReady(row, draft)
  const blockedReasons = ready.reasons.filter(r => r !== ORDER_MESSAGE.sendNotAllowed)
  const hasLocalErrors = Object.keys(errors.items).length > 0 || !!errors.itemsTotal || !!errors.liftReqYn
    || !!errors.deliveryAddress || !!errors.orderNote
  const itemErrors = mergeItemErrors(errors.items, serverErrors.items)
  const headLine = formatPanelHead({
    startDate: model.startDate, orderDate: model.orderDate, phone: p.vendorPhone, assignMethodCode: row.assignMethodCode,
  })
  const saveText = p.saveState === 'saving' ? ORDER_MESSAGE.saving
    : p.saveState === 'saved' ? ORDER_MESSAGE.saved
    : p.saveState === 'error' ? (p.saveError ?? ORDER_MESSAGE.saveFailed)
    : hasLocalErrors ? ORDER_MESSAGE.saveCheckInput : ''

  return (
    <section aria-label={ORDER_MESSAGE.popup.panelHeading} className="flex flex-col gap-3">
      {/* 머리 */}
      <header className="flex flex-col gap-1">
        <h2 ref={p.headingRef} tabIndex={-1} className="text-body font-semibold text-text-primary">
          {model.workTypeName} · {row.vendorName}
        </h2>
        {headLine && <p className="text-caption text-text-muted">{headLine}</p>}
        <OrderStatusLine
          signal={p.signal}
          taskCtx={{ startDate: model.startDate, endDate: model.endDate, vendorName: row.vendorName }}
        />
      </header>

      {/* 회신 결과 */}
      {row.lastReply && (
        <OrderReplyResult
          row={row}
          busy={p.sending}
          onResendWithAvailDate={editable ? p.onResendWithAvailDate : undefined}
          onPickOtherVendor={row.orderStatusCode === 'DECLINED' ? () => setPickingOther(true) : undefined}
        />
      )}
      {pickingOther && (
        <VendorPicker
          row={row}
          taskId={model.taskId}
          onPick={vendorId => { setPickingOther(false); p.onPickVendor(vendorId) }}
        />
      )}

      {/* 안내 줄 */}
      {row.orderStatusCode === 'RESEND_NEEDED' && (
        <Alert kind="warning" role="status">{ORDER_MESSAGE.resendNeeded}</Alert>
      )}
      {p.freshVendor && row.orderStatusCode === 'DRAFT' && (
        <Alert kind="info" role="status">{ORDER_MESSAGE.newVendorSend}</Alert>
      )}
      {lockedText && <Alert kind="info" role="status">{lockedText}</Alert>}
      {serverErrors.panel && <Alert kind="error" role="alert">{serverErrors.panel}</Alert>}

      {/* 발주정보 작성 — 읽기 전용 상태에서는 전체 입력을 막는다 */}
      <fieldset className="flex flex-col gap-3" disabled={!editable}>
        <OrderItemsEditor
          items={draft.items}
          lineErrors={itemErrors}
          totalError={errors.itemsTotal}
          disabled={!editable}
          onChange={items => p.onDraftChange({ items })}
        />

        <OrderDateEditor
          taskId={model.taskId}
          orderDate={model.orderDate}
          deliveryDate={model.deliveryDate}
          startDate={model.startDate}
          disabled={!editable}
          onChange={p.onDatesChange}
        />

        <div className="flex flex-col gap-1">
          <div id={`lift-label-${row.orderVendorId}`} className="text-body-sm font-semibold text-text-primary">
            {ORDER_MESSAGE.lift} <span className="text-text-muted">{ORDER_MESSAGE.required}</span>
          </div>
          <div role="group" aria-labelledby={`lift-label-${row.orderVendorId}`}>
            <Segmented
              size="sm"
              value={draft.liftReqYn}
              options={[{ label: ORDER_MESSAGE.liftYes, value: 'Y' }, { label: ORDER_MESSAGE.liftNo, value: 'N' }]}
              onChange={v => p.onDraftChange({ liftReqYn: v === 'Y' ? 'Y' : 'N' })}
            />
          </div>
          {(errors.liftReqYn || serverErrors.liftReqYn) && (
            <p className="text-caption text-error" role="alert">{errors.liftReqYn ?? serverErrors.liftReqYn}</p>
          )}
        </div>

        <div>
          <Input
            id={`order-address-${row.orderVendorId}`}
            label={ORDER_MESSAGE.address}
            required
            width="fill"
            value={draft.deliveryAddress}
            error={errors.deliveryAddress ?? serverErrors.deliveryAddress}
            onChange={e => p.onDraftChange({ deliveryAddress: e.target.value })}
          />
          <p className="mt-1 text-caption text-text-muted">{ORDER_MESSAGE.popup.addressHint}</p>
        </div>

        <div>
          <Textarea
            id={`order-note-${row.orderVendorId}`}
            label={ORDER_MESSAGE.note}
            className="!max-w-none"
            value={draft.orderNote}
            error={errors.orderNote ?? serverErrors.orderNote}
            onChange={e => p.onDraftChange({ orderNote: e.target.value })}
          />
          <p className="mt-1 text-right text-caption text-text-muted" aria-hidden="true">
            {ORDER_MESSAGE.popup.noteCount(draft.orderNote.length, ORDER_LIMITS.noteMax)}
          </p>
        </div>
      </fieldset>

      {/* 알림톡 미리보기 — 서버 렌더 결과(보낼 수 있는 상태에서만, 접힘이 기본이라 펼칠 때만 조회) */}
      {action.single && p.saveState !== 'saving' && (
        <CollapsibleSection title={ORDER_MESSAGE.previewTitle} open={previewOpen} onToggle={() => setPreviewOpen(v => !v)}>
          <OrderNoticePreview orderVendorId={row.orderVendorId} version={`${row.contentVer}:${row.orderStatusCode}:${row.deliveryDate ?? ''}`} />
        </CollapsibleSection>
      )}

      {/* 저장 상태·발송 */}
      <div className="flex flex-col gap-1">
        <div aria-live="polite" className={p.saveState === 'error' ? 'text-caption text-error' : 'text-caption text-text-muted'}>
          {saveText}
          {p.saveState === 'error' && (
            <Button type="button" variant="ghost" size="sm" onClick={p.onRetrySave}>{ORDER_MESSAGE.saveRetry}</Button>
          )}
        </div>
        {action.single && action.label && (
          <>
            {ready.ok && <StatusHint tone="success" icon="✓">{ORDER_MESSAGE.readyToSend}</StatusHint>}
            <div className="flex flex-wrap gap-2">
              {ready.ok && p.onNextTask && (
                <Button type="button" variant="secondary" size="md" disabled={p.sending} onClick={p.onNextTask}>
                  {ORDER_MESSAGE.nextTask}
                </Button>
              )}
              <Button
                type="button"
                variant="primary"
                size="md"
                loading={p.sending}
                disabled={!ready.ok}
                onClick={p.onSend}
              >
                {action.label}
              </Button>
            </div>
            {blockedReasons.length > 0 && (
              <p className="mt-1 text-caption text-text-muted">{blockedReasons.join(' · ')}</p>
            )}
          </>
        )}
      </div>
    </section>
  )
}
